import { CSSProperties } from 'react'
import { getOrgPreferences } from '../core'
import { colors } from './colors'
import {
  CardThemingConfig,
  CardThemingPrefs,
  ColorConfig,
  ColorType,
  CardThemingOrgPreferences,
  GetCardThemingPrefsOptions,
  UnavailabilityCardThemingPrefs,
} from './types'

interface CardStyles {
  backgroundColor: string
  borderColor: string
  color: string
}

/**
 * Transforms color preferences to CSS styles for card theming
 * @param colorConfig - The color configuration with name and type
 * @param showAsPending - Whether to show the card with pending styling (dashed border, white background)
 * @returns CSS properties for the card
 */
export const transformColorPrefsToStyles = (
  { type, name }: ColorConfig,
  showAsPending: boolean,
): CardStyles => {
  const colorTypeNumber: Record<ColorType, number> = {
    darker: name === 'neutral' ? 750 : 800,
    dark: 700,
    default: name === 'neutral' ? 650 : 600,
    light: 200,
  }

  if (showAsPending) {
    return {
      color: colors.neutral[850],
      borderColor: colors[name][colorTypeNumber[type]],
      backgroundColor: colors.white.white,
    }
  }

  return {
    color: type === 'light' ? colors[name][900] : colors.white.white,
    borderColor: colors.transparent.transparent,
    backgroundColor: colors[name][colorTypeNumber[type]],
  }
}

/**
 * Gets CSS styles for a card based on color configuration
 * @param colors - Record of color configurations by field value
 * @param showAsPending - Whether to show as pending (dashed border style)
 * @param fieldValue - The value of the field to look up the color for
 * @returns CSS properties for the card
 */
export const getCardStylesFromConfig = (
  colors: Record<string, ColorConfig>,
  showAsPending: boolean,
  fieldValue?: string,
): CSSProperties => {
  const colorConfig: ColorConfig =
    fieldValue && colors[fieldValue]
      ? colors[fieldValue]
      : colors['Default']
        ? colors['Default']
        : { name: 'neutral', type: 'default' }

  const { backgroundColor, color, borderColor } = transformColorPrefsToStyles(
    colorConfig,
    showAsPending,
  )

  return {
    backgroundColor,
    borderColor,
    color,
    borderWidth: '1px',
    borderStyle: 'solid',
  }
}

const defaultCardContent = { primary: 'Name', secondary: 'Notes' }

const defaultColorConfigs = (
  colorConfig: ColorConfig,
): Record<string, Record<string, ColorConfig>> => ({
  Default: { Default: colorConfig },
})

const hasCardThemingConfig = (
  config?: CardThemingConfig,
): config is CardThemingConfig => !!config && Object.keys(config).length > 0

/**
 * Reads card theming preferences from org preferences for any card config key,
 * falling back to caller-provided defaults and then to a hardcoded baseline.
 */
export const getCardThemingPrefs = ({
  themingKey,
  orgPrefs,
  defaultOrgPrefs,
  defaults,
}: GetCardThemingPrefsOptions): CardThemingPrefs => {
  const prefs = orgPrefs ?? (getOrgPreferences() as CardThemingOrgPreferences)
  const cards =
    prefs?.web?.theming?.[themingKey] ??
    defaultOrgPrefs?.web?.theming?.[themingKey]

  const hardcodedDefaults = {
    cardContent: defaults?.cardContent ?? defaultCardContent,
    colorFieldSelected: defaults?.colorFieldSelected ?? 'Default',
    colorConfigs:
      defaults?.colorConfigs ??
      defaultColorConfigs({ name: 'neutral', type: 'default' }),
  }

  const defaultCard =
    defaultOrgPrefs?.web?.theming?.[themingKey] ?? hardcodedDefaults

  const selectedCard = hasCardThemingConfig(cards) ? cards : defaultCard

  const cardContent =
    selectedCard.cardContent ??
    defaultCard.cardContent ??
    hardcodedDefaults.cardContent
  const colorFieldSelected =
    selectedCard.colorFieldSelected ??
    defaultCard.colorFieldSelected ??
    hardcodedDefaults.colorFieldSelected
  const colorConfigs = selectedCard.colorConfigs

  return {
    cardContent,
    colorFieldSelected,
    colors:
      colorConfigs?.[colorFieldSelected] ??
      colorConfigs?.Default ??
      defaultCard.colorConfigs?.[colorFieldSelected] ??
      defaultCard.colorConfigs?.Default ??
      hardcodedDefaults.colorConfigs.Default,
  }
}

/**
 * Reads unavailability card theming preferences from org preferences,
 * falling back to caller-provided defaults and then to a hardcoded baseline.
 *
 * @deprecated Use `getCardThemingPrefs` with `themingKey: 'unavailabilityCards'` instead.
 */
export const getUnavailabilityCardThemingPrefs = (
  orgPrefs?: CardThemingOrgPreferences,
  defaultOrgPrefs?: CardThemingOrgPreferences,
): UnavailabilityCardThemingPrefs =>
  getCardThemingPrefs({
    themingKey: 'unavailabilityCards',
    orgPrefs,
    defaultOrgPrefs,
    defaults: {
      colorConfigs: defaultColorConfigs({ name: 'cyan', type: 'default' }),
    },
  })
